/**
 * GedcomPreviewTable Component
 *
 * Displays parsed INDI records in a table format.
 * Shows GEDCOM ID, name, gender, birth, and death information.
 *
 * Part of Phase 4 - Slice 2, 3, & 4: Parser Integration + Date Parsing
 */

import { useTranslation } from 'react-i18next';
import type { ParsedPerson } from '../parsers/indiParser';
import type { DateQualifier } from '../parsers/dateParser';

interface GedcomPreviewTableProps {
  persons: ParsedPerson[];
}

export function GedcomPreviewTable({ persons }: GedcomPreviewTableProps) {
  const { t, i18n } = useTranslation('common');

  const getGenderIcon = (gender: 'male' | 'female' | 'other') => {
    switch (gender) {
      case 'male':
        return '♂';
      case 'female':
        return '♀';
      case 'other':
        return '?';
    }
  };

  const getGenderLabel = (gender: 'male' | 'female' | 'other') => {
    switch (gender) {
      case 'male':
        return t('gedcom.male', 'Male');
      case 'female':
        return t('gedcom.female', 'Female');
      case 'other':
        return t('gedcom.other', 'Other/Unknown');
    }
  };

  /**
   * Format ISO date to readable format with locale support (e.g., "24 May 1819" / "24 مايو 1819")
   */
  const formatDateForDisplay = (isoDate: string): string => {
    const [year, month, day] = isoDate.split('-').map(Number);

    // Month names mapping for English and Arabic
    const monthNames: Record<string, { en: string; ar: string }> = {
      '01': { en: 'Jan', ar: 'يناير' },
      '02': { en: 'Feb', ar: 'فبراير' },
      '03': { en: 'Mar', ar: 'مارس' },
      '04': { en: 'Apr', ar: 'أبريل' },
      '05': { en: 'May', ar: 'مايو' },
      '06': { en: 'Jun', ar: 'يونيو' },
      '07': { en: 'Jul', ar: 'يوليو' },
      '08': { en: 'Aug', ar: 'أغسطس' },
      '09': { en: 'Sep', ar: 'سبتمبر' },
      '10': { en: 'Oct', ar: 'أكتوبر' },
      '11': { en: 'Nov', ar: 'نوفمبر' },
      '12': { en: 'Dec', ar: 'ديسمبر' }
    };

    const monthKey = month.toString().padStart(2, '0');
    const monthName = i18n.language === 'ar'
      ? monthNames[monthKey]?.ar
      : monthNames[monthKey]?.en;

    // Return in natural order for both languages
    // English: "24 May 1819"
    // Arabic: "24 مايو 1819" (same order, RTL handles direction)
    return `${day} ${monthName} ${year}`;
  };

  /**
   * Get translated qualifier label
   */
  const getQualifierLabel = (qualifier: DateQualifier): string => {
    switch (qualifier) {
      case 'ABT':
        return t('gedcom.about', 'About');
      case 'BEF':
        return t('gedcom.before', 'Before');
      case 'AFT':
        return t('gedcom.after', 'After');
      case 'CAL':
        return t('gedcom.calculated', 'Calculated');
      case 'EST':
        return t('gedcom.estimated', 'Estimated');
    }
  };

  /**
   * Format date display with qualifier support
   */
  const formatDateDisplay = (
    date: string | null,
    year: number | null,
    qualifier: DateQualifier | null
  ): string => {
    if (!year && !date) {
      return '—';
    }

    let displayValue = '';

    if (date) {
      // Full date available
      displayValue = formatDateForDisplay(date);
    } else if (year) {
      // Year-only
      displayValue = year.toString();
    }

    if (qualifier && displayValue) {
      // Prepend qualifier
      return `${getQualifierLabel(qualifier)} ${displayValue}`;
    }

    return displayValue || '—';
  };

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-xs sm:text-sm">
          <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
            <tr>
              <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-semibold text-slate-700 dark:text-slate-300">
                {t('gedcom.gedcomId', 'GEDCOM ID')}
              </th>
              <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-semibold text-slate-700 dark:text-slate-300">
                {t('gedcom.name', 'Name')}
              </th>
              <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-semibold text-slate-700 dark:text-slate-300">
                {t('gedcom.gender', 'Gender')}
              </th>
              <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-semibold text-slate-700 dark:text-slate-300">
                {t('gedcom.birth', 'Birth')}
              </th>
              <th className="px-3 sm:px-4 py-2 sm:py-3 text-left font-semibold text-slate-700 dark:text-slate-300">
                {t('gedcom.death', 'Death')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {persons.map((person, index) => (
              <tr
                key={person.gedcomId}
                className={`${
                  index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-900'
                } hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors`}
              >
                <td className="px-3 sm:px-4 py-2 sm:py-3 font-mono text-xs text-slate-600 dark:text-slate-400">
                  {person.gedcomId}
                </td>
                <td className="px-3 sm:px-4 py-2 sm:py-3 font-medium text-slate-900 dark:text-white">
                  {person.displayName}
                  {person.surname === null && person.givenName && (
                    <span className="text-xs text-slate-500 dark:text-slate-400 ml-1">(no surname)</span>
                  )}
                </td>
                <td className="px-3 sm:px-4 py-2 sm:py-3">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <span className="text-base sm:text-lg" title={getGenderLabel(person.gender)}>
                      {getGenderIcon(person.gender)}
                    </span>
                    <span className="text-slate-700 dark:text-slate-300">{getGenderLabel(person.gender)}</span>
                  </div>
                </td>
                <td className="px-3 sm:px-4 py-2 sm:py-3 text-slate-700 dark:text-slate-300">
                  {formatDateDisplay(person.birthDate, person.birthYear, person.birthQualifier)}
                </td>
                <td className="px-3 sm:px-4 py-2 sm:py-3 text-slate-700 dark:text-slate-300">
                  {formatDateDisplay(person.deathDate, person.deathYear, person.deathQualifier)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
